'use client';

import { PointerEvent, useRef, useState } from 'react';

type Props = {
  src: string;
  ariaLabel: string;
};

const layerStyle = {
  position: 'absolute',
  inset: 0,
} as const;

const videoStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: 'opacity 0.3s ease',
} as const;

// The case study `coverVideo` as a hover layer over the SSR'd cover <Image> (sibling, server-rendered,
// stays the LCP element). By default the cover shows; the video is mounted (and downloaded) on the
// first hover only, plays once per hover (no loop; stops on its last frame) and fades out / pauses on
// leave — the next hover resumes, or restarts it once it has ended. Touch devices keep the cover.
export const CaseStudyCoverVideo = ({ src, ariaLabel }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleEnter = (event: PointerEvent) => {
    // Hover only: a tap on touch screens fires pointerenter too.
    if (event.pointerType !== 'mouse') return;
    setMounted(true);
    setHovered(true);
    // Already mounted: resume. On the first hover `autoPlay` starts it once it can play.
    videoRef.current?.play().catch(() => undefined);
  };

  const handleLeave = () => {
    setHovered(false);
    videoRef.current?.pause();
  };

  return (
    <div style={layerStyle} onPointerEnter={handleEnter} onPointerLeave={handleLeave}>
      {mounted ? (
        <video
          ref={videoRef}
          style={{ ...videoStyle, opacity: hovered ? 1 : 0 }}
          autoPlay
          muted
          playsInline
          preload="metadata"
          aria-label={ariaLabel}
        >
          <source src={src} type="video/mp4" />
        </video>
      ) : null}
    </div>
  );
};
